/**
 * Enhanced Error Debugger
 * Provides comprehensive error tracking and debugging capabilities
 */

import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from './logger';

export interface ErrorContext {
  // Request details
  method?: string;
  url?: string;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;

  // Response details
  status?: number;
  statusText?: string;
  responseData?: any;
  responseHeaders?: Record<string, string>;

  // Timing
  timestamp: string;
  duration?: number;

  // Environment
  userAgent: string;
  viewport: string;
  onlineStatus: boolean;

  // Error details
  errorMessage: string;
  errorStack?: string;
  errorType: string;

  // Additional context
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;

  // Correlation
  correlationId: string;
  requestId?: string;
}

export interface EnhancedError extends Error {
  context?: ErrorContext;
  isApiError?: boolean;
  originalError?: any;
}

/**
 * Generate a unique correlation ID for tracking errors
 */
export function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract comprehensive error context from an Axios error
 */
export function extractErrorContext(
  error: AxiosError | Error,
  additionalContext?: Partial<ErrorContext>
): ErrorContext {
  const correlationId = generateCorrelationId();
  const timestamp = new Date().toISOString();

  const baseContext: ErrorContext = {
    timestamp,
    correlationId,
    errorMessage: error.message,
    errorStack: error.stack,
    errorType: error.constructor.name,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    onlineStatus: navigator.onLine,
    ...additionalContext,
  };

  // If it's an Axios error, extract request/response details
  if ('isAxiosError' in error && error.isAxiosError) {
    const axiosError = error as AxiosError;

    // Request details
    if (axiosError.config) {
      baseContext.method = axiosError.config.method?.toUpperCase();
      baseContext.url = axiosError.config.url;
      baseContext.params = axiosError.config.params;

      // Sanitize sensitive data from request body
      if (axiosError.config.data) {
        try {
          const data = typeof axiosError.config.data === 'string'
            ? JSON.parse(axiosError.config.data)
            : axiosError.config.data;
          baseContext.data = sanitizeSensitiveData(data);
        } catch {
          baseContext.data = '[Unable to parse request data]';
        }
      }

      // Extract headers (sanitize auth tokens)
      if (axiosError.config.headers) {
        baseContext.headers = sanitizeHeaders(
          axiosError.config.headers as Record<string, string>
        );
      }
    }

    // Response details
    if (axiosError.response) {
      baseContext.status = axiosError.response.status;
      baseContext.statusText = axiosError.response.statusText;
      baseContext.responseData = sanitizeSensitiveData(axiosError.response.data);
      baseContext.responseHeaders = sanitizeHeaders(
        axiosError.response.headers as Record<string, string>
      );

      // Extract request ID from response headers if available
      if (axiosError.response.headers) {
        baseContext.requestId =
          axiosError.response.headers['x-request-id'] ||
          axiosError.response.headers['x-correlation-id'];
      }
    }

    // Calculate duration if available
    if (axiosError.config?.headers && 'x-request-start' in axiosError.config.headers) {
      const startTime = parseInt(axiosError.config.headers['x-request-start'] as string);
      if (!isNaN(startTime)) {
        baseContext.duration = Date.now() - startTime;
      }
    }
  }

  return baseContext;
}

/**
 * Sanitize sensitive data from objects (passwords, tokens, etc.)
 */
function sanitizeSensitiveData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sensitiveKeys = [
    'password', 'token', 'secret', 'apiKey', 'api_key',
    'authorization', 'csrf', 'session', 'cookie'
  ];

  const sanitized = Array.isArray(data) ? [...data] : { ...data };

  for (const key in sanitized) {
    if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeSensitiveData(sanitized[key]);
    }
  }

  return sanitized;
}

/**
 * Sanitize headers (remove auth tokens)
 */
function sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
  const sanitized = { ...headers };
  const sensitiveHeaders = ['authorization', 'cookie', 'x-csrf-token', 'x-api-key'];

  for (const key of Object.keys(sanitized)) {
    if (sensitiveHeaders.some(sensitive => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    }
  }

  return sanitized;
}

/**
 * Create a detailed error report
 */
export function createErrorReport(context: ErrorContext): string {
  const sections: string[] = [];

  // Header
  sections.push('═══════════════════════════════════════════════════════════');
  sections.push('🔴 ERROR DIAGNOSTIC REPORT');
  sections.push('═══════════════════════════════════════════════════════════');
  sections.push('');

  // Error Information
  sections.push('📋 ERROR INFORMATION');
  sections.push('───────────────────────────────────────────────────────────');
  sections.push(`Timestamp:       ${context.timestamp}`);
  sections.push(`Correlation ID:  ${context.correlationId}`);
  sections.push(`Error Type:      ${context.errorType}`);
  sections.push(`Error Message:   ${context.errorMessage}`);
  if (context.requestId) {
    sections.push(`Request ID:      ${context.requestId}`);
  }
  sections.push('');

  // Request Information
  if (context.method && context.url) {
    sections.push('🌐 REQUEST DETAILS');
    sections.push('───────────────────────────────────────────────────────────');
    sections.push(`Method:          ${context.method}`);
    sections.push(`URL:             ${context.url}`);
    if (context.params && Object.keys(context.params).length > 0) {
      sections.push(`Parameters:      ${JSON.stringify(context.params, null, 2)}`);
    }
    if (context.data) {
      sections.push(`Request Body:    ${JSON.stringify(context.data, null, 2)}`);
    }
    if (context.headers && Object.keys(context.headers).length > 0) {
      sections.push(`Headers:         ${JSON.stringify(context.headers, null, 2)}`);
    }
    if (context.duration) {
      sections.push(`Duration:        ${context.duration}ms`);
    }
    sections.push('');
  }

  // Response Information
  if (context.status) {
    sections.push('📨 RESPONSE DETAILS');
    sections.push('───────────────────────────────────────────────────────────');
    sections.push(`Status:          ${context.status} ${context.statusText || ''}`);
    if (context.responseData) {
      sections.push(`Response Body:   ${JSON.stringify(context.responseData, null, 2)}`);
    }
    if (context.responseHeaders && Object.keys(context.responseHeaders).length > 0) {
      sections.push(`Headers:         ${JSON.stringify(context.responseHeaders, null, 2)}`);
    }
    sections.push('');
  }

  // Environment Information
  sections.push('💻 ENVIRONMENT');
  sections.push('───────────────────────────────────────────────────────────');
  sections.push(`User Agent:      ${context.userAgent}`);
  sections.push(`Viewport:        ${context.viewport}`);
  sections.push(`Online Status:   ${context.onlineStatus ? 'Online' : 'Offline'}`);
  if (context.component) {
    sections.push(`Component:       ${context.component}`);
  }
  if (context.action) {
    sections.push(`Action:          ${context.action}`);
  }
  sections.push('');

  // Stack Trace
  if (context.errorStack) {
    sections.push('📚 STACK TRACE');
    sections.push('───────────────────────────────────────────────────────────');
    sections.push(context.errorStack);
    sections.push('');
  }

  sections.push('═══════════════════════════════════════════════════════════');

  return sections.join('\n');
}

/**
 * Log an enhanced error with full context
 */
export function logEnhancedError(
  error: AxiosError | Error,
  additionalContext?: Partial<ErrorContext>
): EnhancedError {
  const context = extractErrorContext(error, additionalContext);
  const report = createErrorReport(context);

  // Log to console in development
  if (import.meta.env.DEV) {
    console.group(`🔴 Error: ${context.errorMessage}`);
    console.error(report);
    console.error('Original Error:', error);
    console.groupEnd();
  }

  // Log using centralized logger
  logger.error(context.errorMessage, {
    correlationId: context.correlationId,
    context,
    report,
  });

  // Create enhanced error object
  const enhancedError: EnhancedError = new Error(context.errorMessage);
  enhancedError.context = context;
  enhancedError.isApiError = 'isAxiosError' in error;
  enhancedError.originalError = error;
  enhancedError.stack = context.errorStack;

  return enhancedError;
}

/**
 * Create a request tracking interceptor
 */
export function createRequestTracker() {
  const pendingRequests = new Map<string, { startTime: number; config: AxiosRequestConfig }>();

  return {
    onRequest: (config: AxiosRequestConfig) => {
      const correlationId = generateCorrelationId();
      const startTime = Date.now();

      // Add tracking headers
      if (config.headers) {
        config.headers['x-correlation-id'] = correlationId;
        config.headers['x-request-start'] = startTime.toString();
      }

      // Store request for tracking
      pendingRequests.set(correlationId, { startTime, config });

      if (import.meta.env.DEV) {
        logger.debug(`→ ${config.method?.toUpperCase()} ${config.url}`, {
          correlationId,
          params: config.params,
        });
      }

      return config;
    },

    onResponse: (response: AxiosResponse) => {
      const correlationId = response.config.headers?.['x-correlation-id'] as string;
      const request = pendingRequests.get(correlationId);

      if (request) {
        const duration = Date.now() - request.startTime;
        pendingRequests.delete(correlationId);

        if (import.meta.env.DEV) {
          logger.debug(`← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            correlationId,
            duration: `${duration}ms`,
            status: response.status,
          });
        }
      }

      return response;
    },

    onError: (error: AxiosError) => {
      const correlationId = error.config?.headers?.['x-correlation-id'] as string;
      const request = correlationId ? pendingRequests.get(correlationId) : undefined;

      if (request) {
        pendingRequests.delete(correlationId);
      }

      // Log enhanced error
      const enhancedError = logEnhancedError(error, {
        correlationId,
      });

      return Promise.reject(enhancedError);
    },
  };
}

/**
 * Export utility to check if error is enhanced
 */
export function isEnhancedError(error: unknown): error is EnhancedError {
  return error instanceof Error && 'context' in error && error.context !== undefined;
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (isEnhancedError(error) && error.context) {
    const { status, responseData } = error.context;

    // Map common HTTP status codes to user-friendly messages
    switch (status) {
      case 400:
        return responseData?.detail || 'Invalid request. Please check your input.';
      case 401:
        return 'You are not authenticated. Please log in.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return responseData?.detail || 'This operation conflicts with existing data.';
      case 422:
        return responseData?.detail || 'Validation failed. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return responseData?.detail || 'Internal server error. Our team has been notified.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service unavailable. Please try again later.';
      case 504:
        return 'Gateway timeout. The server took too long to respond.';
      default:
        return responseData?.detail || error.message || 'An unexpected error occurred.';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred.';
}
